import { useForm, yupResolver } from "@mantine/form";
import {
  ExpirationMode,
  CreateDiscountCodeInput,
  useCreateDiscountCodeMutation,
  DiscountCodeType,
  // useCreateServiceAllotmentMutation,
} from "src/generated/graphql";
import { memo } from "react";
import { Button, Group, useMantineTheme } from "@mantine/core";
import DiscountFormFields, {
  discountFormSchema,
} from "../components/FormFields";
import { IIDAndName } from "src/utils/helperTypes";
import { ExpirationFields } from "../components/ExpirationField";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "src/utils/constants";

export interface IDiscountForm
  extends Omit<CreateDiscountCodeInput, "locations"> {
  locations: string[] | undefined;
}

export type CreateDiscountFormProps = {
  locationsList: IIDAndName[] | undefined;
  onRefetch: () => void;
  close: () => void;
};

const CreateDiscountForm = ({
  locationsList = [],
  onRefetch,
  close,
}: CreateDiscountFormProps) => {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const [createDiscount, { loading }] = useCreateDiscountCodeMutation({
    onCompleted: (resp) => {
      close();
      if (resp.createDiscountCode) {
        navigate(`${ROUTES.DISCOUNTS}/${resp.createDiscountCode.id}`);
      }
      //onRefetch();
    },
  });

  const form = useForm<IDiscountForm>({
    initialValues: {
      name: "",
      description: undefined,
      locations: undefined,
      code: "",
      value: 0,
      isPercent: true,
      discountCodeType: DiscountCodeType.Product,
      startAt: new Date(),
      expiresAt: undefined,
      mode: ExpirationMode.Never,
    },
    validate: yupResolver(discountFormSchema),
  });

  const handleSubmit = async (values: Required<IDiscountForm>) => {
    const {
      mode,
      expiresAt,
      expirationUnit,
      expirationValue,
      locations = [],
      ...restValues
    } = values;
    let discountValues: CreateDiscountCodeInput = {
      mode,
      locations: locations?.map((location) => ({ id: location })),
      ...restValues,
    };
    if (mode === ExpirationMode.Date) {
      discountValues.expiresAt = expiresAt;
    }
    if (mode === ExpirationMode.Duration) {
      discountValues.expirationUnit = expirationUnit;
      discountValues.expirationValue = expirationValue;
    }

    await createDiscount({
      variables: {
        data: discountValues,
      },
    });
  };

  const isFormUpdated = form.isDirty() && form.isTouched();

  return (
    <form
      onSubmit={form.onSubmit((values) =>
        handleSubmit(values as Required<IDiscountForm>)
      )}
    >
      <DiscountFormFields<IDiscountForm> locations={locationsList} form={form}>
        <ExpirationFields<IDiscountForm> form={form} />
      </DiscountFormFields>

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={close}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          type="submit"
          disabled={!isFormUpdated}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
          loading={loading}
        >
          Create
        </Button>
      </Group>
    </form>
  );
};

export default memo(CreateDiscountForm);
